import { ethers, Contract } from 'ethers';
import {
  SFM_TEAMS_CONTRACT_ADDRESS,
  SFM_TEAMS_CONTRACT_ABI,
  SFM_TEAMS_MEMBERS_CONTRACT_ADDRESS,
} from './SFM-TeamsConstants';
import { toast } from 'react-hot-toast';

// Create a team
export async function createTeam(
  name,
  description,
  rules,
  leaderURI,
  memberURI,
  requiresApproval,
  joiningFee,
  initialFund
) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );
  if (chainID === 84532) {
    const joiningFeesInWei = ethers.utils.parseEther(joiningFee.toString(), 18);
    const transaction = await contract.createTeam(
      name,
      description,
      rules,
      leaderURI,
      memberURI,
      requiresApproval,
      joiningFeesInWei,
      { value: joiningFeesInWei }
    );
    const receipt = await transaction.wait();

    // Search for the TeamCreated event in the receipt
    let teamIdVal;
    const eventName = 'TeamCreated';
    for (let event of receipt.events) {
      if (event.event === eventName) {
        teamIdVal = parseInt(event.args[0]._hex, 16);
        break;
      }
    }
    initialFund !== '' && (await fundTeam(teamIdVal, initialFund));
    console.log('Team created');
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

// Get all teams data
export async function getAllTeams() {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );
  if (chainID === 84532) {
    const teams = await contract.getAllTeams();
    return teams;
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

// Get my team data
export async function getMyTeam() {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );
  if (chainID === 84532) {
    const team = await contract.getMyTeam();
    return team;
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

export async function joinTeam(teamId) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );
  if (chainID === 84532) {
    try {
      const team = await contract.teams(teamId);
      const value = team.joiningFee.toString(); // 0.1 ETH joining fee
      const transaction = await contract.requestJoinTeam(teamId, { value });
      await transaction.wait();
    } catch (err) {
      throw err;
    }
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

// Remove a team member
export async function removeTeamMember(teamId, memberAddress) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );

  if (chainID === 84532) {
    const transaction = await contract.removeTeamMember(teamId, memberAddress);
    await transaction.wait();
    console.log('Team member removed');
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

// Leave the team
export async function leaveTeam() {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );

  if (chainID === 84532) {
    const transaction = await contract.leaveTeam();
    await transaction.wait();
    console.log('Left the team');
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

// Fund the team
export async function fundTeam(teamId, amount) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );

  if (chainID === 84532) {
    const value = ethers.utils.parseEther(amount.toString());
    const transaction = await contract.fundTeam(teamId, { value });
    await transaction.wait();
    console.log('Team funded');
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

// Get team balance
export async function getTeamBalance(teamId) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );

  if (chainID === 84532) {
    const balance = await contract.getTeamBalance(teamId);
    console.log('Team balance:', balance.toString());
    return balance;
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

// Function to accept a join request
export async function acceptJoinRequest(teamId, player) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );

  if (chainID === 84532) {
    const approveJoinRequest = await contract.approveJoinRequest(
      teamId,
      player
    );
    await approveJoinRequest.wait();
    console.log('Join request approved successfully!');
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

// Get All Join Requests For Team
export async function getAllJoinRequestsForTeam(teamId) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );

  if (chainID === 84532) {
    const joinRequests = await contract.getJoinRequestsForTeam(teamId);
    console.log('Team Join Requests:', joinRequests);
    return joinRequests;
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

// Get an NFT token id
export async function getNFTtokenId() {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );
  if (chainID === 84532) {
    try {
      const tokenId = await contract.getTokenId();
      return tokenId;
    } catch (err) {
      console.log('You do not own any NFTs yet');
    }
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

// Get an NFT token id from members contract
export async function getMemberTokenId() {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_MEMBERS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );
  if (chainID === 84532) {
    try {
      const tokenId = await contract.getTokenId();
      return tokenId;
    } catch (err) {
      console.log('You do not own any NFTs yet');
    }
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}
// Get an NFT token id from members contract
export async function getMemberTokenIdWithAddress(address) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;

  // Create a new Wallet instance with the private key and connect it to the provider
  const treasuryWallet = new ethers.Wallet(
    process.env.REACT_APP_TREASURY_WALLET_PRIVATE_KEY
  );
  const treasuryWalletConnected = treasuryWallet.connect(provider);

  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    treasuryWalletConnected
  );

  if (chainID === 84532) {
    try {
      const tokenId = await contract.getTokenIdWithAddress(address);
      return tokenId;
    } catch (err) {
      console.log('You do not own any NFTs yet');
    }
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

// Get my team data
export async function getTeam(teamId) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );
  if (chainID === 84532) {
    const team = await contract.teams(teamId);
    console.log('Selected Team:', team);
    return team;
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

// Get Team members given team id
export async function getTeamMembers(teamId) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );
  if (chainID === 84532) {
    const teamMembers = await contract.getTeamMembers(teamId);
    return teamMembers;
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

/**
 * Function to check if the current player joins any team
 */
export async function isPlayerInTeam() {
  //check if the current player in team
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const playerAddress = await signer.getAddress();

  // Create a new Wallet instance with the private key and connect it to the provider
  const treasuryWallet = new ethers.Wallet(
    process.env.REACT_APP_TREASURY_WALLET_PRIVATE_KEY
  );
  const treasuryWalletConnected = treasuryWallet.connect(provider);

  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    treasuryWalletConnected
  );

  if (chainID === 84532) {
    const isPlayerInTeam = await contract.isPlayerInTeam(playerAddress);
    return isPlayerInTeam;
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

/**
 * Function to get team id of the current user
 */
export async function getTeamId() {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    signer
  );
  if (chainID === 84532) {
    const teamId = await contract.getTeamId();
    return teamId;
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

/**
 * Function to pay game fee from the teams balance
 */
export async function payGameFee(teamId, amount) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;

  // Create a new Wallet instance with the private key and connect it to the provider
  const treasuryWallet = new ethers.Wallet(
    process.env.REACT_APP_TREASURY_WALLET_PRIVATE_KEY_SFM
  );
  const treasuryWalletConnected = treasuryWallet.connect(provider);

  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    treasuryWalletConnected
  );

  const gameTicketAmountHex = ethers.utils.parseEther(amount.toString(), 18);
  const gameTicketAmountDec = parseInt(gameTicketAmountHex._hex, 16);

  if (chainID === 84532) {
    const response = await contract.payGameFee(teamId, gameTicketAmountDec, {
      gasLimit: 5000000,
      gasPrice: ethers.utils.parseUnits('10', 'gwei'),
    });
    const receipt = await response.wait();
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

/**
 * Function to update player and team scores in the team mode
 */
export async function updateTeamScore(score) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  const signer = provider.getSigner();
  const playerAddress = await signer.getAddress();
  console.log(`playerAddress: ${playerAddress}`);
  // Create a new Wallet instance with the private key and connect it to the provider
  const treasuryWallet = new ethers.Wallet(
    process.env.REACT_APP_TREASURY_WALLET_PRIVATE_KEY
  );
  const treasuryWalletConnected = treasuryWallet.connect(provider);

  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    treasuryWalletConnected
  );

  if (chainID === 84532) {
    const response = await contract.updateScore(playerAddress, score, {
      gasLimit: 5000000,
      gasPrice: ethers.utils.parseUnits('10', 'gwei'),
    });
    const receipt = await response.wait();
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

/**
 * Function to update player and team scores in the team mode
 */
export async function getTokenURI(tokenId) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;

  // Create a new Wallet instance with the private key and connect it to the provider
  const treasuryWallet = new ethers.Wallet(
    process.env.REACT_APP_TREASURY_WALLET_PRIVATE_KEY
  );
  const treasuryWalletConnected = treasuryWallet.connect(provider);

  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    treasuryWalletConnected
  );

  if (chainID === 84532) {
    await contract.tokenURI(tokenId);
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

/**
 * Function to get player team score
 */
export async function getPlayerTeamScore(playerAddress) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;

  // Create a new Wallet instance with the private key and connect it to the provider
  const treasuryWallet = new ethers.Wallet(
    process.env.REACT_APP_TREASURY_WALLET_PRIVATE_KEY
  );
  const treasuryWalletConnected = treasuryWallet.connect(provider);

  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    treasuryWalletConnected
  );

  if (chainID === 84532) {
    await contract.getPlayerTeamScore(playerAddress);
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

/**
 * Function to get team players
 */
export async function getTeamPlayers(teamId) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;

  // Create a new Wallet instance with the private key and connect it to the provider
  const treasuryWallet = new ethers.Wallet(
    process.env.REACT_APP_TREASURY_WALLET_PRIVATE_KEY
  );
  const treasuryWalletConnected = treasuryWallet.connect(provider);

  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    treasuryWalletConnected
  );

  if (chainID === 84532) {
    const teamPlayers = await contract.getTeamPlayers(teamId);
    return teamPlayers;
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

/**
 * Function to get all teams count
 */
export async function getTeamsCount() {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;

  // Create a new Wallet instance with the private key and connect it to the provider
  const treasuryWallet = new ethers.Wallet(
    process.env.REACT_APP_TREASURY_WALLET_PRIVATE_KEY
  );
  const treasuryWalletConnected = treasuryWallet.connect(provider);

  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    treasuryWalletConnected
  );

  if (chainID === 84532) {
    const topTeams = await contract.getTeamsCount();
    return topTeams;
  } else {
    toast.error('Please connect to Base Sepolia Testnet');
  }
}

/**
 * @function
 * @name isLeader
 * @description Returns a car struct for a given ID.
 * @param {address} _userAddress - The user address
 * @returns {bool} A bool that indicates whether this user is a leader in any team or not
 */
export const isLeader = async (_userAddress) => {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;

  if (chainID != 84532) {
    toast.error('Please connect to Base Sepolia Testnet');
  }
  try {
    const treasuryWallet = new ethers.Wallet(
      process.env.REACT_APP_TREASURY_WALLET_PRIVATE_KEY
    );
    const treasuryWalletConnected = treasuryWallet.connect(provider);

    const contract = new Contract(
      SFM_TEAMS_CONTRACT_ADDRESS,
      SFM_TEAMS_CONTRACT_ABI,
      treasuryWalletConnected
    );
    const response = await contract.isLeader(_userAddress);

    return response;
  } catch (err) {
    throw err;
  }
};

/*
// Function to get top n teams in score
export async function getTeamsTopScore(n) {
  const provider = new ethers.providers.Web3Provider(window.ethereum);
  const chainID = (await provider.getNetwork()).chainId;
  
  // Create a new Wallet instance with the private key and connect it to the provider
  const treasuryWallet = new ethers.Wallet(process.env.REACT_APP_TREASURY_WALLET_PRIVATE_KEY);
  const treasuryWalletConnected = treasuryWallet.connect(provider);

  const contract = new Contract(
    SFM_TEAMS_CONTRACT_ADDRESS,
    SFM_TEAMS_CONTRACT_ABI,
    treasuryWalletConnected
  );

  if (chainID === 84532) {
    const topTeams = await contract.getTeamsTopScore(n);
    return topTeams;
  } else {
    toast.error("Please connect to Base Sepolia Testnet");
  }
}
*/
